/**
 * CartDrawer — right-side slide-in shopping cart.
 *
 * Single source of truth for the purchase flow in the SaaS. Opens from
 * the CartButton in any header (landing, /planes, in-app TopBar). Holds:
 *
 *   * List of items with quantity controls and per-line subtotals.
 *   * Empty state with a CTA back to the catalog.
 *   * Footer with subtotal, "Vaciar carrito", T&C checkbox, and the
 *     "Pagar con Culqi" button that runs the sequential checkout.
 *
 * The drawer is wired up to:
 *   - `useCartStore`   — local persisted cart state.
 *   - `useBuyPlan`     — fires one Culqi charge per cart item.
 *   - `useAuthStore`   — anonymous shoppers are bounced to /login with
 *     the cart preserved (it's in localStorage).
 *
 * Open/close is controlled by `useCartUiStore` so the CartButton in any
 * header (or anywhere else in the app) can simply call `open()`.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  CheckCircle2,
  Infinity as InfinityIcon,
  Loader2,
  Lock,
  Minus,
  Plus,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import { useBuyPlan } from '@/features/billing/useBuyPlan';
import { formatPrice, type CheckoutResponse } from '@/features/billing/types';
import { cartTotalCents, useCartStore } from '@/stores/cartStore';
import { useCartUiStore } from '@/stores/cartUiStore';
import { useAuthStore } from '@/stores/authStore';

export function CartDrawer() {
  const open = useCartUiStore((s) => s.open);
  const setOpen = useCartUiStore((s) => s.setOpen);
  const items = useCartStore((s) => s.items);
  const setQuantity = useCartStore((s) => s.setQuantity);
  const remove = useCartStore((s) => s.remove);
  const clear = useCartStore((s) => s.clear);

  const isLoggedIn = !!useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const buyPlan = useBuyPlan();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [completed, setCompleted] = useState<CheckoutResponse[] | null>(null);

  const total = cartTotalCents(items);
  const itemCount = items.reduce((acc, it) => acc + it.quantity, 0);

  async function onCheckout() {
    if (!isLoggedIn) {
      // Cart is in localStorage so it survives the login round-trip.
      setOpen(false);
      navigate('/login?next=' + encodeURIComponent('/app/billing'));
      return;
    }
    if (!acceptedTerms) return;
    const results: CheckoutResponse[] = [];
    for (const item of items) {
      for (let i = 0; i < item.quantity; i++) {
        const r = await buyPlan.buy(item.plan);
        if (!r) {
          // Stop on first failure so we don't keep charging if the card
          // got declined. Items already paid stay paid (idempotent on
          // Culqi charge id).
          return;
        }
        results.push(r);
      }
    }
    setCompleted(results);
    clear();
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(6px)' }}
        />
        <Dialog.Content
          className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {/* ----- Header ----- */}
          <div
            className="flex items-center justify-between border-b px-5 py-4"
            style={{ borderColor: 'var(--border-subtle)' }}
          >
            <div className="flex items-center gap-2.5">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-[var(--radius)]"
                style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
              >
                <ShoppingCart size={16} strokeWidth={1.8} />
              </span>
              <div>
                <Dialog.Title
                  className="text-base"
                  style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
                >
                  Carrito
                </Dialog.Title>
                <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  {itemCount === 0
                    ? 'Vacío'
                    : `${itemCount} ${itemCount === 1 ? 'producto' : 'productos'}`}
                </div>
              </div>
            </div>
            <Dialog.Close
              className="rounded-[var(--radius-sm)] p-1.5 transition-colors hover:bg-[var(--surface-2)]"
              aria-label="Cerrar carrito"
            >
              <X size={16} strokeWidth={1.8} />
            </Dialog.Close>
          </div>

          {/* ----- Body ----- */}
          <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
            {completed ? (
              <CompletedView results={completed} onClose={() => setOpen(false)} onReset={() => setCompleted(null)} />
            ) : items.length === 0 ? (
              <EmptyView onClose={() => setOpen(false)} />
            ) : (
              <ul className="flex flex-col gap-3">
                {items.map((it) => (
                  <li
                    key={it.plan.id}
                    className="rounded-[var(--radius-md)] border p-3"
                    style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius)]"
                        style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
                      >
                        {it.plan.is_subscription ? (
                          <InfinityIcon size={16} strokeWidth={2} />
                        ) : (
                          <FierroIcon size={16} />
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm" style={{ color: 'var(--text-primary)' }}>
                          {it.plan.name}
                        </div>
                        <div className="text-[11.5px]" style={{ color: 'var(--text-muted)' }}>
                          {it.plan.is_subscription
                            ? `Suscripción · ${it.plan.duration_days} días`
                            : `${it.plan.tokens} fierros incluidos`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(it.plan.id)}
                        className="rounded-[var(--radius-sm)] p-1 transition-colors hover:bg-[var(--surface-3)]"
                        aria-label="Quitar del carrito"
                        title="Quitar del carrito"
                      >
                        <Trash2 size={14} strokeWidth={1.8} style={{ color: 'var(--danger)' }} />
                      </button>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      {/* Quantity controls — subscriptions are locked at 1
                          since adding two would just compound the expiry. */}
                      {it.plan.is_subscription ? (
                        <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                          x 1
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setQuantity(it.plan.id, it.quantity - 1)}
                            disabled={it.quantity <= 1}
                            aria-label="Quitar uno"
                          >
                            <Minus size={12} strokeWidth={1.8} />
                          </Button>
                          <span className="w-7 text-center font-mono tabular text-sm">{it.quantity}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setQuantity(it.plan.id, it.quantity + 1)}
                            aria-label="Agregar uno"
                          >
                            <Plus size={12} strokeWidth={1.8} />
                          </Button>
                        </div>
                      )}
                      <span className="font-mono tabular text-sm" style={{ color: 'var(--text-primary)' }}>
                        {formatPrice(it.plan.price_cents * it.quantity, it.plan.currency)}
                      </span>
                    </div>
                  </li>
                ))}

                <li className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={clear}
                    className="mt-1 flex items-center gap-1.5 text-[12px] underline-offset-2 hover:underline"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    <Trash2 size={12} strokeWidth={1.8} /> Vaciar carrito
                  </button>
                  <Link
                    to="/carrito"
                    onClick={() => setOpen(false)}
                    className="mt-1 flex items-center gap-1.5 text-[12px] underline-offset-2 no-underline hover:underline"
                    style={{ color: 'var(--accent)' }}
                  >
                    Ver carrito completo →
                  </Link>
                </li>
              </ul>
            )}
          </div>

          {/* ----- Footer ----- */}
          {!completed && items.length > 0 && (
            <div
              className="border-t px-5 pb-5 pt-4"
              style={{ borderColor: 'var(--border-subtle)', background: 'var(--surface-1)' }}
            >
              <div className="mb-3 flex items-baseline justify-between">
                <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                  Total
                </span>
                <span className="font-mono tabular text-2xl" style={{ color: 'var(--text-primary)' }}>
                  {formatPrice(total)}
                </span>
              </div>

              <label
                className="mb-3 flex cursor-pointer items-start gap-2.5 rounded-[var(--radius-sm)] border p-2.5 text-[12px]"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5 accent-[var(--accent)]"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.currentTarget.checked)}
                />
                <span>
                  Acepto los{' '}
                  <Link
                    to="/terminos"
                    target="_blank"
                    className="underline-offset-2 hover:underline"
                    style={{ color: 'var(--accent)' }}
                  >
                    términos y condiciones
                  </Link>{' '}
                  y autorizo el cargo en mi tarjeta vía Culqi.
                </span>
              </label>

              {buyPlan.error && (
                <div
                  className="mb-3 rounded-[var(--radius-sm)] border p-2 text-[12px]"
                  style={{
                    background: 'rgba(220,38,38,0.08)',
                    borderColor: 'rgba(220,38,38,0.4)',
                    color: '#fca5a5',
                  }}
                >
                  {buyPlan.error}
                </div>
              )}

              <Button
                variant="primary"
                size="md"
                onClick={onCheckout}
                disabled={
                  !acceptedTerms ||
                  buyPlan.busyPlanId !== null ||
                  (isLoggedIn && !buyPlan.ready)
                }
                className="w-full"
              >
                {buyPlan.busyPlanId !== null ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Lock size={14} strokeWidth={1.8} />
                )}
                {!isLoggedIn
                  ? 'Iniciar sesión para pagar'
                  : buyPlan.busyPlanId !== null
                    ? 'Procesando…'
                    : `Pagar ${formatPrice(total)}`}
              </Button>

              <div className="mt-2 text-center text-[10.5px]" style={{ color: 'var(--text-muted)' }}>
                Pago seguro vía Culqi (Niubiz / VisaNet). Aceptamos tarjeta y Yape.
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Inner views
// ---------------------------------------------------------------------------

function EmptyView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div
        className="mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}
      >
        <ShoppingCart size={26} strokeWidth={1.5} />
      </div>
      <h3 className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        Tu carrito está vacío
      </h3>
      <p className="mt-2 max-w-[280px] text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
        Añade un pack de fierros o una suscripción desde el catálogo.
      </p>
      <div className="mt-5 flex gap-2">
        <Link to="/planes" onClick={onClose} className="no-underline">
          <Button variant="primary" size="sm">
            Ver planes
          </Button>
        </Link>
      </div>
    </div>
  );
}

function CompletedView({
  results,
  onClose,
  onReset,
}: {
  results: CheckoutResponse[];
  onClose: () => void;
  onReset: () => void;
}) {
  const tokens = results.reduce((acc, r) => acc + r.tokens_credited, 0);
  const lastSub = results.findLast((r) => r.subscription_expires_at);
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div
        className="mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'rgba(34,197,94,0.18)', color: '#86efac' }}
      >
        <CheckCircle2 size={28} strokeWidth={1.8} />
      </div>
      <h3 className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
        ¡Pago confirmado!
      </h3>
      <ul className="mt-3 space-y-1 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
        {tokens > 0 && (
          <li>
            <FierroIcon size={11} /> +{tokens} fierros acreditados
          </li>
        )}
        {lastSub && (
          <li>
            <InfinityIcon size={11} strokeWidth={2} style={{ color: 'var(--accent)' }} /> Suscripción
            activa hasta {String(lastSub.subscription_expires_at).slice(0, 10)}
          </li>
        )}
      </ul>
      <div className="mt-5 flex gap-2">
        <Button variant="ghost" size="sm" onClick={onReset}>
          Seguir comprando
        </Button>
        <Link to="/app/billing" onClick={onClose} className="no-underline">
          <Button variant="primary" size="sm">
            Ver mi cuenta
          </Button>
        </Link>
      </div>
    </div>
  );
}
